import React, { useEffect, useMemo, useState } from 'react';
import { Button, PlayerSearchInput, SectionHeader } from '../common';
import AddPlayerDropdown from '../courts/AddPlayerDropdown';
import { getPlayerFullName, getPlayerNameColor, formatSessionGamesCountLabel } from '../../utils/playerUtils';
import { getInitials } from '../../utils/nameUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';
import {
  PEG_BOARD_GAME_ALGORITHM_MANUAL,
  PEG_BOARD_GAME_ALGORITHM_RANDOM,
  getEligiblePool,
  playedLineupKeys,
  suggestRandomDoubles,
  suggestRandomDoublesAnyPicker,
  validateSelection,
} from '../../utils/pegBoard';

const compactSearchClass = '[&_.form-input]:py-1.5 [&_.form-input]:text-sm [&_.form-input]:min-h-0';

function waitlistPosition(playerId, queueOrderIds) {
  if (!playerId || !Array.isArray(queueOrderIds)) return null;
  const index = queueOrderIds.indexOf(playerId);
  return index >= 0 ? index + 1 : null;
}

function PegBoardSlotSearch({
  placeholder,
  selectedId,
  eligibleIds,
  playersById,
  selectedIds,
  onSelectId,
  onClear,
  disabled,
  queueOrderIds,
}) {
  const availablePlayers = useMemo(() => {
    const taken = new Set((selectedIds || []).filter((id) => id && id !== selectedId));
    return (eligibleIds || [])
      .filter((id) => !taken.has(id))
      .map((id) => playersById[id])
      .filter(Boolean);
  }, [eligibleIds, playersById, selectedIds, selectedId]);

  const selectedPlayer = selectedId ? playersById[selectedId] || null : null;
  if (selectedPlayer) {
    return (
      <div className="rounded-lg border border-border px-3 py-2">
        <p className="text-xs text-text-muted">{placeholder}</p>
        <PlayerMini
          player={selectedPlayer}
          queuePosition={waitlistPosition(selectedPlayer.id, queueOrderIds)}
          onRemove={onClear}
          removeDisabled={disabled}
        />
      </div>
    );
  }

  return (
    <PlayerSearchInput
      placeholder={placeholder}
      availablePlayers={availablePlayers}
      selectedPlayer={null}
      onSelectPlayer={(player) => onSelectId(player.id)}
      onRemovePlayer={onClear}
      disabled={disabled}
      showGamesPlayed
      queueOrderIds={queueOrderIds}
      className={`w-full min-w-0 ${compactSearchClass}`}
    />
  );
}

function PegBoardHostSlot({
  placeholder,
  selectedId,
  onSelectId,
  onClear,
  disabled,
  playersById,
  queueOrderIds,
  waitlistPlayers,
  occupiedPlayers,
  pegBoardQueue,
  selectedIds,
}) {
  const [open, setOpen] = useState(false);
  const selectedPlayer = selectedId ? playersById[selectedId] || null : null;
  const availablePlayers = useMemo(
    () =>
      (waitlistPlayers || []).filter(
        (player) => player && (!selectedIds.includes(player.id) || player.id === selectedId)
      ),
    [waitlistPlayers, selectedIds, selectedId]
  );

  if (selectedPlayer) {
    return (
      <div className="rounded-lg border border-border px-3 py-2">
        <p className="text-xs text-text-muted">{placeholder}</p>
        <PlayerMini
          player={selectedPlayer}
          queuePosition={waitlistPosition(selectedPlayer.id, queueOrderIds)}
          onRemove={onClear}
          removeDisabled={disabled}
        />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="group flex min-h-[52px] w-full cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border-medium bg-surface-subtle px-3 py-2 text-left transition-colors hover:border-primary hover:bg-surface disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="text-sm font-medium text-text-muted group-hover:text-primary">+ {placeholder}</span>
      </button>
      <AddPlayerDropdown
        isOpen={open}
        onClose={() => setOpen(false)}
        onSelectPlayer={(playerId) => onSelectId(playerId)}
        availablePlayers={availablePlayers}
        occupiedPlayers={occupiedPlayers}
        pegBoardQueue={pegBoardQueue}
        subtitle={placeholder}
        showEloTooltip={false}
      />
    </>
  );
}

function PlayerMini({ player, queuePosition = null, onRemove = null, removeDisabled = false }) {
  if (!player) return null;
  const displayName = getPlayerFullName(player);
  return (
    <span className="inline-flex w-full items-center gap-2 min-w-0">
      {queuePosition != null ? (
        <span className="w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-text-muted">
          {queuePosition}
        </span>
      ) : null}
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-text-inverse ${getGenderAvatarBg(player.gender)}`}
      >
        {getInitials(displayName)}
      </span>
      <span className={`min-w-0 truncate text-sm font-semibold ${getPlayerNameColor(player.gender)}`}>
        {displayName}
      </span>
      <span className="ml-auto shrink-0 text-xs tabular-nums text-text-muted" title="Games this session">
        {formatSessionGamesCountLabel(player)}
      </span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={removeDisabled}
          className={`shrink-0 text-lg font-bold leading-none ${
            removeDisabled
              ? 'cursor-not-allowed text-text-muted'
              : 'cursor-pointer text-danger-dark hover:text-danger'
          }`}
          aria-label={`Remove ${displayName}`}
          title="Remove player"
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/**
 * Shared Peg Board pick controls (picker slot, partner/opponents, random + confirm).
 * Used by the empty-court pick modal.
 */
export function PegBoardPickForm({
  waitingList = [],
  selectionWindow = 6,
  playersById = {},
  occupiedIds = [],
  occupiedPlayers = [],
  currentUserId = null,
  isSessionHost = false,
  onConfirm,
  confirming = false,
  onPlaced,
  showHeader = true,
  matchHistory = [],
}) {
  const playedKeys = useMemo(() => playedLineupKeys(matchHistory), [matchHistory]);
  const pool = useMemo(
    () => getEligiblePool(waitingList, selectionWindow),
    [waitingList, selectionWindow]
  );
  const queueOrderIds = useMemo(
    () => (waitingList || []).map((entry) => (typeof entry === 'string' ? entry : entry?.id)).filter(Boolean),
    [waitingList]
  );
  const waitlistPlayers = useMemo(
    () => queueOrderIds.map((id) => playersById[id]).filter(Boolean),
    [queueOrderIds, playersById]
  );
  const pegBoardQueue = useMemo(
    () => ({ waitlistIds: queueOrderIds, selectionWindow: pool.window }),
    [queueOrderIds, pool.window]
  );
  const waitlistPicker = playersById[pool.pickerId];
  const pickerName = waitlistPicker ? getPlayerFullName(waitlistPicker) : 'Next player';
  const canSubmitRole = isSessionHost || (!!currentUserId && currentUserId === pool.pickerId);

  const [hostPickerId, setHostPickerId] = useState(null);
  const [partnerId, setPartnerId] = useState('');
  const [opp1, setOpp1] = useState('');
  const [opp2, setOpp2] = useState('');
  const [error, setError] = useState(null);
  const [draftFromRandom, setDraftFromRandom] = useState(false);

  useEffect(() => {
    const waitlistSet = new Set(queueOrderIds);
    if (isSessionHost) {
      setHostPickerId((id) => {
        if (id == null) return null;
        if (id === '') return '';
        return waitlistSet.has(id) ? id : null;
      });
      setPartnerId((id) => (id && waitlistSet.has(id) ? id : ''));
      setOpp1((id) => (id && waitlistSet.has(id) ? id : ''));
      setOpp2((id) => (id && waitlistSet.has(id) ? id : ''));
      return;
    }
    const eligible = new Set(pool.eligibleIds);
    setPartnerId((id) => (id && !eligible.has(id) ? '' : id));
    setOpp1((id) => (id && !eligible.has(id) ? '' : id));
    setOpp2((id) => (id && !eligible.has(id) ? '' : id));
  }, [queueOrderIds, pool.eligibleIds, isSessionHost]);

  const pickerId = isSessionHost
    ? (hostPickerId == null ? pool.pickerId : hostPickerId)
    : pool.pickerId;
  const hostEligibleIds = queueOrderIds;
  const selectedIds = [pickerId, partnerId, opp1, opp2];
  const validation = validateSelection({
    pickerId,
    partnerId,
    opp1,
    opp2,
    eligibleIds: isSessionHost ? hostEligibleIds : pool.eligibleIds,
    occupiedIds,
    hostOverride: isSessionHost,
  });

  const hostSlotProps = {
    playersById,
    queueOrderIds,
    waitlistPlayers,
    occupiedPlayers,
    pegBoardQueue,
    selectedIds,
    disabled: confirming,
  };

  const handleRandom = () => {
    setError(null);
    const occupied = new Set((occupiedIds || []).filter(Boolean));
    const waitlistFree = queueOrderIds.filter((id) => !occupied.has(id));
    const keepWindowPicker = !isSessionHost || pickerId === pool.pickerId;
    if (isSessionHost && !pickerId) {
      if (waitlistFree.length < 4) {
        setError('Need at least four queue players to suggest a game.');
        return;
      }
      const arranged = suggestRandomDoublesAnyPicker(waitlistFree, {
        playersById,
        seed: `${Date.now()}`,
        playedKeys,
      });
      if (!arranged.ok) {
        setError('Could not suggest a game from the queue.');
        return;
      }
      setHostPickerId(arranged.team1[0] || '');
      setPartnerId(arranged.team1[1] || '');
      setOpp1(arranged.team2[0] || '');
      setOpp2(arranged.team2[1] || '');
      setDraftFromRandom(true);
      return;
    }
    const randomPool = keepWindowPicker ? pool.eligibleIds : waitlistFree;
    const suggestion = suggestRandomDoubles(randomPool, pickerId, {
      playersById,
      seed: `${Date.now()}`,
      playedKeys,
    });
    if (!suggestion.ok) {
      setError(
        keepWindowPicker
          ? 'Could not suggest a game from the current window.'
          : 'Could not suggest a game from the queue.'
      );
      return;
    }
    if (isSessionHost) setHostPickerId(suggestion.team1[0] || '');
    setPartnerId(suggestion.team1[1] || '');
    setOpp1(suggestion.team2[0] || '');
    setOpp2(suggestion.team2[1] || '');
    setDraftFromRandom(true);
  };

  const handleConfirm = async (algorithm) => {
    setError(null);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }
    const result = await onConfirm?.({
      expectedPickerId: pickerId,
      partnerId,
      opp1Id: opp1,
      opp2Id: opp2,
      algorithm,
    });
    if (result?.ok === false) {
      setError(result.message || 'Could not place this game.');
      return;
    }
    setHostPickerId(null);
    setPartnerId('');
    setOpp1('');
    setOpp2('');
    setDraftFromRandom(false);
    onPlaced?.();
  };

  if (!pool.canPick) {
    return (
      <>
        {showHeader ? <SectionHeader title="Peg Board pick" size="sm" className="!mb-2" /> : null}
        <p className="text-sm text-text-secondary">
          Need at least four players in the queue before the next game can be picked.
        </p>
      </>
    );
  }

  const slotSearch = (placeholder, selectedId, setId) =>
    isSessionHost ? (
      <PegBoardHostSlot
        placeholder={placeholder}
        selectedId={selectedId}
        onSelectId={(id) => {
          setDraftFromRandom(false);
          setId(id);
        }}
        onClear={() => {
          setDraftFromRandom(false);
          setId('');
        }}
        {...hostSlotProps}
      />
    ) : (
      <PegBoardSlotSearch
        placeholder={placeholder}
        selectedId={selectedId}
        eligibleIds={pool.eligibleIds.filter((id) => id !== pool.pickerId)}
        playersById={playersById}
        selectedIds={selectedIds}
        queueOrderIds={queueOrderIds}
        onSelectId={(id) => {
          setDraftFromRandom(false);
          setId(id);
        }}
        onClear={() => {
          setDraftFromRandom(false);
          setId('');
        }}
        disabled={!canSubmitRole || confirming}
      />
    );

  return (
    <>
      {showHeader ? (
        <SectionHeader
          title={`${pickerName} is up to pick`}
          size="sm"
          className="!mb-2"
        />
      ) : null}

      <div className="mb-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">Team 1</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {isSessionHost ? (
            <PegBoardHostSlot
              placeholder="Picker"
              selectedId={pickerId}
              onSelectId={(id) => {
                setDraftFromRandom(false);
                setHostPickerId(id);
              }}
              onClear={() => {
                setDraftFromRandom(false);
                setHostPickerId('');
              }}
              {...hostSlotProps}
            />
          ) : (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
              <p className="text-xs text-text-muted">Picker</p>
              <PlayerMini player={waitlistPicker} queuePosition={1} />
            </div>
          )}
          {slotSearch('Partner', partnerId, setPartnerId)}
        </div>
      </div>

      <div className="mb-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">Team 2</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {slotSearch('Opponent 1', opp1, setOpp1)}
          {slotSearch('Opponent 2', opp2, setOpp2)}
        </div>
      </div>

      {error ? (
        <p className="mb-3 text-sm text-danger-dark">{error}</p>
      ) : null}

      {!canSubmitRole ? (
        <p className="text-sm text-text-muted">Waiting for {pickerName} or the host to confirm.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="small"
            onClick={handleRandom}
            disabled={confirming}
          >
            Random Pick
          </Button>
          <Button
            type="button"
            variant="primary"
            size="small"
            onClick={() => handleConfirm(
              draftFromRandom ? PEG_BOARD_GAME_ALGORITHM_RANDOM : PEG_BOARD_GAME_ALGORITHM_MANUAL
            )}
            disabled={confirming || !validation.ok}
          >
            {confirming ? 'Placing…' : 'Confirm game'}
          </Button>
        </div>
      )}
    </>
  );
}
